/**
 * Calculates the Levenshtein distance between two strings.
 * The Levenshtein distance is the minimum number of single-character edits
 * (insertions, deletions, or substitutions) required to change one string into the other.
 *
 * @param str1 - First string to compare
 * @param str2 - Second string to compare
 * @returns The Levenshtein distance between the two strings
 *
 * @example
 * levenshteinDistance('kitten', 'sitting') // Returns 3
 * levenshteinDistance('hello', 'hello') // Returns 0
 */
export function levenshteinDistance(str1: string, str2: string): number {
  const len1 = str1.length;
  const len2 = str2.length;
  const matrix: number[][] = [];

  // Initialize matrix
  for (let i = 0; i <= len1; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= len2; j++) {
    matrix[0][j] = j;
  }

  // Fill matrix
  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j] + 1      // deletion
        );
      }
    }
  }

  return matrix[len1][len2];
}

/**
 * Calculates the similarity percentage between two strings using Levenshtein distance.
 * Returns a value between 0 and 1, where 1 means identical strings and 0 means completely different.
 *
 * @param str1 - First string to compare
 * @param str2 - Second string to compare
 * @returns A similarity score between 0 and 1
 *
 * @example
 * calculateSimilarity('kitten', 'sitting') // Returns ~0.57
 * calculateSimilarity('hello', 'hello') // Returns 1.0
 * calculateSimilarity('abc', 'xyz') // Returns 0
 */
export function calculateSimilarity(str1: string, str2: string): number {
  const distance = levenshteinDistance(str1, str2);
  const maxLen = Math.max(str1.length, str2.length);

  // Avoid division by zero
  if (maxLen === 0) return 1;

  return 1 - (distance / maxLen);
}
